import { useEffect, useRef, useState } from "react";

/**
 * useMicLevel(listening)
 * - mesure un niveau audio (0..1) via WebAudio analyser
 * - fonctionne seulement quand "listening" = true
 */
export default function useMicLevel(listening) {
  const [level, setLevel] = useState(0);

  const rafRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const streamRef = useRef(null);
  const dataRef = useRef(null);

  useEffect(() => {
    const stopAll = async () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;

      try {
        if (sourceRef.current) sourceRef.current.disconnect();
      } catch {}

      try {
        if (analyserRef.current) analyserRef.current.disconnect();
      } catch {}

      sourceRef.current = null;
      analyserRef.current = null;

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }

      if (audioCtxRef.current) {
        try {
          await audioCtxRef.current.close();
        } catch {}
        audioCtxRef.current = null;
      }

      dataRef.current = null;
      setLevel(0);
    };

    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });

        streamRef.current = stream;

        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        const audioCtx = new AudioCtx();
        audioCtxRef.current = audioCtx;

        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 2048; // bonne précision
        analyser.smoothingTimeConstant = 0.85; // plus "smooth"
        analyserRef.current = analyser;

        const source = audioCtx.createMediaStreamSource(stream);
        sourceRef.current = source;

        source.connect(analyser);

        const bufferLength = analyser.fftSize;
        const data = new Uint8Array(bufferLength);
        dataRef.current = data;

        const tick = () => {
          if (!analyserRef.current || !dataRef.current) return;

          analyserRef.current.getByteTimeDomainData(dataRef.current);

          // RMS amplitude (0..1)
          let sumSquares = 0;
          for (let i = 0; i < dataRef.current.length; i++) {
            const v = (dataRef.current[i] - 128) / 128; // -1..1
            sumSquares += v * v;
          }
          const rms = Math.sqrt(sumSquares / dataRef.current.length);

          // mapping pour un rendu "Shazam": boost léger, clamp
          const boosted = Math.min(1, Math.max(0, rms * 2.2));

          setLevel(boosted);
          rafRef.current = requestAnimationFrame(tick);
        };

        tick();
      } catch (e) {
        console.error("Mic level error:", e);
        // si refus micro, on reste à 0
        setLevel(0);
      }
    };

    if (listening) start();
    else stopAll();

    return () => stopAll();
  }, [listening]);

  return level; // 0..1
}
